import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Card from "../components/Card";
import Sidebar from "../components/Sidebar";
import Button from "../components/Button";
import { mlmAPI } from "../services/api";

const ChevronDownIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

const TIME_OPTIONS = ["Last 7 Days", "Last 30 Days", "Last 90 Days", "Last Year", "All Time"];

const LEVEL_OPTIONS = [
  "All Levels",
  ...Array.from({ length: 20 }, (_, i) => `Level ${i + 1}`),
];

function getSinceDate(timeFilter) {
  if (timeFilter === "All Time") return null;
  const now = new Date();
  const d = new Date(now.getTime());
  switch (timeFilter) {
    case "Last 7 Days":
      d.setUTCDate(d.getUTCDate() - 7);
      break;
    case "Last 30 Days":
      d.setUTCDate(d.getUTCDate() - 30);
      break;
    case "Last 90 Days":
      d.setUTCDate(d.getUTCDate() - 90);
      break;
    case "Last Year":
      d.setUTCFullYear(d.getUTCFullYear() - 1);
      break;
    default:
      return null;
  }
  return d;
}

const FilterDropdown = ({ label, value, options, onChange }) => (
  <div className="relative min-w-[160px]">
    <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">{label}</p>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none w-full bg-[var(--bg-card)] border border-primary/30 rounded-xl px-4 py-2.5 pr-10 text-[var(--text-primary)] text-sm font-medium focus:outline-none focus:border-primary transition-all cursor-pointer hover:border-primary/50"
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
    <ChevronDownIcon className="w-4 h-4 text-[var(--text-primary)] absolute right-3 bottom-2.5 pointer-events-none" />
  </div>
);

const MetricHighlightCard = ({ title, value, subtext }) => (
  <Card className="p-6 hover:shadow-lg hover:shadow-primary/10 transition-all duration-300">
    <div className="space-y-2">
      <p className="text-xs text-[var(--text-primary)] opacity-60 uppercase tracking-wider">
        {title}
      </p>
      <h3 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)]">{value}</h3>
      <p className="text-sm text-[var(--text-primary)] opacity-40">{subtext}</p>
    </div>
  </Card>
);

const ChartCard = ({ title, children }) => (
  <Card className="p-6">
    <h3 className="text-base md:text-lg font-bold text-[var(--text-primary)] mb-6">{title}</h3>
    {children}
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[var(--bg-main)] border border-primary/30 rounded-lg p-3 shadow-xl">
        <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">{label}</p>
        <p className="text-sm font-bold text-[var(--text-primary)]">
          {typeof payload[0].value === "number"
            ? `$${payload[0].value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
            : payload[0].value}
        </p>
      </div>
    );
  }
  return null;
};

const RecruitTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[var(--bg-main)] border border-primary/30 rounded-lg p-3 shadow-xl">
        <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">{label}</p>
        <p className="text-sm font-bold text-[var(--text-primary)]">
          New members: {payload[0].value}
        </p>
      </div>
    );
  }
  return null;
};

const LevelWiseBusiness = () => {
  const [timeFilter, setTimeFilter] = useState("Last 30 Days");
  const [levelFilter, setLevelFilter] = useState("All Levels");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [levelWiseRaw, setLevelWiseRaw] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await mlmAPI.getLevelWiseReferrals(20);
      setLevelWiseRaw(data && typeof data === "object" ? data : {});
    } catch (e) {
      setError(e?.error || e?.message || "Failed to load level-wise business.");
      setLevelWiseRaw({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const flatMembers = useMemo(() => {
    const rows = [];
    Object.entries(levelWiseRaw || {}).forEach(([levelKey, members]) => {
      const levelNum = parseInt(String(levelKey).replace("level", ""), 10);
      const L = Number.isNaN(levelNum) ? 0 : levelNum;
      (members || []).forEach((m) => {
        rows.push({
          id: m.id,
          level: L,
          name: m.name,
          totalEarnings: Number(m.totalEarnings || 0),
          isActive: m.isActive !== false,
          createdAt: m.createdAt ? new Date(m.createdAt) : null,
          downlineCount: Number(m.downlineCount || 0),
        });
      });
    });
    return rows;
  }, [levelWiseRaw]);

  const since = useMemo(() => getSinceDate(timeFilter), [timeFilter]);

  const timeFilteredMembers = useMemo(() => {
    if (!since) return flatMembers;
    return flatMembers.filter((m) => {
      if (!m.createdAt || Number.isNaN(m.createdAt.getTime())) return false;
      return m.createdAt >= since;
    });
  }, [flatMembers, since]);

  const scopedMembers = useMemo(() => {
    if (levelFilter === "All Levels") return timeFilteredMembers;
    const n = parseInt(levelFilter.replace(/\D/g, ""), 10);
    if (!Number.isFinite(n)) return timeFilteredMembers;
    return timeFilteredMembers.filter((m) => m.level === n);
  }, [timeFilteredMembers, levelFilter]);

  const volumeByLevelData = useMemo(() => {
    const sums = Array.from({ length: 20 }, (_, i) => ({
      level: `L${i + 1}`,
      levelNum: i + 1,
      volume: 0,
      count: 0,
    }));
    timeFilteredMembers.forEach((m) => {
      if (m.level >= 1 && m.level <= 20) {
        const idx = m.level - 1;
        sums[idx].volume += m.totalEarnings;
        sums[idx].count += 1;
      }
    });
    if (levelFilter !== "All Levels") {
      const n = parseInt(levelFilter.replace(/\D/g, ""), 10);
      if (Number.isFinite(n) && n >= 1 && n <= 20) {
        return [sums[n - 1]];
      }
    }
    return sums;
  }, [timeFilteredMembers, levelFilter]);

  const joinTrendData = useMemo(() => {
    const monthKey = (d) =>
      `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    const monthLabel = (d) =>
      d.toLocaleString(undefined, { month: "short", year: "numeric", timeZone: "UTC" });
    const map = new Map();
    const membersForTrend =
      levelFilter === "All Levels"
        ? timeFilteredMembers
        : timeFilteredMembers.filter(
            (m) => m.level === parseInt(levelFilter.replace(/\D/g, ""), 10),
          );
    membersForTrend.forEach((m) => {
      if (!m.createdAt || Number.isNaN(m.createdAt.getTime())) return;
      const k = monthKey(m.createdAt);
      map.set(k, (map.get(k) || 0) + 1);
    });
    const end = new Date();
    const start = new Date(
      Date.UTC(end.getUTCFullYear(), end.getUTCMonth() - 17, 1, 0, 0, 0, 0),
    );
    const out = [];
    for (let d = new Date(start); d <= end; d.setUTCMonth(d.getUTCMonth() + 1)) {
      const k = monthKey(d);
      out.push({
        month: monthLabel(d),
        sortKey: k,
        recruits: map.get(k) || 0,
      });
    }
    return out;
  }, [timeFilteredMembers, levelFilter]);

  const summary = useMemo(() => {
    const totalVolume = scopedMembers.reduce((s, m) => s + m.totalEarnings, 0);
    const activeParticipants = scopedMembers.filter((m) => m.isActive).length;
    const levelsWithMembers = new Set(scopedMembers.map((m) => m.level)).size;
    const avgPerLevel =
      levelsWithMembers > 0 ? totalVolume / levelsWithMembers : 0;
    return {
      totalVolume,
      activeParticipants,
      levelsWithMembers: levelsWithMembers || (levelFilter === "All Levels" ? 0 : 1),
      avgPerLevel,
    };
  }, [scopedMembers, levelFilter]);

  const maxBar = useMemo(
    () => Math.max(1, ...volumeByLevelData.map((d) => d.volume)),
    [volumeByLevelData],
  );

  const maxTrend = useMemo(
    () => Math.max(1, ...joinTrendData.map((d) => d.recruits)),
    [joinTrendData],
  );

  const timeHint = since
    ? `Including downline who joined on or after ${since.toISOString().slice(0, 10)} (UTC).`
    : "Including your full downline (all join dates).";

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)]">
      <Sidebar />
      <div className="flex-1 p-4 pt-16 md:p-8 md:pt-8 md:ml-64">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] mb-1">
              Level-wise details
            </h1>
            <p className="text-sm text-[var(--text-primary)] opacity-60">
              Live view from your referral tree: earnings totals by depth and new-member join trend.
              Bot vs trading affiliate percentages are on{" "}
              <Link
                to="/mlm-level-details"
                className="text-primary font-semibold underline-offset-2 hover:underline"
              >
                Level Details
              </Link>
              .
            </p>
            <p className="text-xs text-[var(--text-primary)] opacity-50 mt-2">{timeHint}</p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
            <FilterDropdown
              label="Time period (by join date)"
              value={timeFilter}
              options={TIME_OPTIONS}
              onChange={setTimeFilter}
            />
            <FilterDropdown
              label="Level scope"
              value={levelFilter}
              options={LEVEL_OPTIONS}
              onChange={setLevelFilter}
            />
            <Button variant="secondary" onClick={fetchData} disabled={loading}>
              {loading ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
        </div>

        {error ? (
          <Card className="p-6 mb-6 border border-red-500/30">
            <p className="text-red-400 font-medium">{error}</p>
            <Button className="mt-4" onClick={fetchData}>
              Retry
            </Button>
          </Card>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartCard title="Team earnings sum by level (USDT)">
            {loading ? (
              <div className="h-[250px] md:h-[300px] flex items-center justify-center text-[var(--text-primary)] opacity-60">
                Loading chart…
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250} className="md:!h-[300px]">
                <BarChart data={volumeByLevelData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis
                    dataKey="level"
                    stroke="#22c55e"
                    style={{ fontSize: "11px" }}
                  />
                  <YAxis
                    stroke="#22c55e"
                    style={{ fontSize: "11px" }}
                    domain={[0, maxBar]}
                    tickFormatter={(v) =>
                      v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}k` : `${v}`
                    }
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="volume"
                    fill="url(#goldGradient)"
                    radius={[12, 12, 0, 0]}
                  />
                  <defs>
                    <linearGradient id="goldGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity={1} />
                      <stop offset="100%" stopColor="#16a34a" stopOpacity={0.85} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard title="New downline joins by month (count)">
            {loading ? (
              <div className="h-[250px] md:h-[300px] flex items-center justify-center text-[var(--text-primary)] opacity-60">
                Loading chart…
              </div>
            ) : joinTrendData.length === 0 ? (
              <div className="h-[250px] md:h-[300px] flex items-center justify-center text-sm text-[var(--text-primary)] opacity-60 text-center px-4">
                No join activity in this period for the selected level scope.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250} className="md:!h-[300px]">
                <AreaChart data={joinTrendData}>
                  <defs>
                    <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#16a34a" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis
                    dataKey="month"
                    stroke="#22c55e"
                    style={{ fontSize: "11px" }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    stroke="#22c55e"
                    style={{ fontSize: "11px" }}
                    domain={[0, maxTrend]}
                    allowDecimals={false}
                  />
                  <Tooltip content={<RecruitTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="recruits"
                    stroke="#22c55e"
                    strokeWidth={3}
                    fill="url(#areaGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        <div className="mt-8">
          <h2 className="text-lg md:text-xl font-bold text-[var(--text-primary)] mb-4">
            Summary metrics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <MetricHighlightCard
              title="Total team earnings (sum)"
              value={
                loading
                  ? "…"
                  : `$${summary.totalVolume.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
              }
              subtext="Sum of member totalEarnings in the current filters"
            />
            <MetricHighlightCard
              title="Avg. earnings per level (with members)"
              value={
                loading
                  ? "…"
                  : `$${summary.avgPerLevel.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
              }
              subtext={`Across ${summary.levelsWithMembers} level(s) with at least one member`}
            />
            <MetricHighlightCard
              title="Active participants"
              value={loading ? "…" : String(summary.activeParticipants)}
              subtext={`${scopedMembers.length} member(s) in filtered set`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LevelWiseBusiness;
